import { faKey, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import authShieldError from 'assets/img/spot-illustrations/shield.png';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { Component } from 'react';
import { Navigate } from 'react-router-dom';
import AuthService from 'services/auth.service';
import TwoFAForm from 'components/modules/auth/TwoFAForm';
import { secureStorage } from 'services/secure-storage.service';
import entraidlogologo from 'assets/img/icons/entraid.png';
import manageenginelogo from 'assets/img/icons/manageengine.png';
import { getBackendUrl } from '../../../config';

// eslint-disable-next-line
type Props = {};

type State = {
  redirect: string | null;
  username: string;
  password: string;
  loading: boolean;
  message: string;
  requiresTFA: boolean;
  qrCodeURL: string;
  tfaConfirmed: boolean;
  rememberMe: boolean;
};

export default class Login extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const rememberedUser = AuthService.getRememberedUser();

    this.handleLogin = this.handleLogin.bind(this);
    this.handleTFA = this.handleTFA.bind(this);

    this.state = {
      redirect: null,
      username: rememberedUser ? rememberedUser.username : '',
      password: '',
      loading: false,
      message: '',
      requiresTFA: false,
      qrCodeURL: '',
      tfaConfirmed: false,
      rememberMe: !!rememberedUser
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRedirectPath(user: any): string {
    // Usuario especial Aurea
    if (user.username === 'aureavirtualagent') {
      return '/authentication/virtualagent';
    }

    // Lógica existente para otros usuarios
    const avatar = secureStorage.getItem('avatar');
    if (avatar === 'avatar1') {
      return '/system-management/system-dashboard';
    } else if (avatar === 'avatar2') {
      return '/';
    }

    return '/';
  }

  componentDidMount() {
    const rememberedUser = AuthService.getRememberedUser();
    if (rememberedUser) {
      // Pre-rellenar el campo de usuario
      this.setState({
        username: rememberedUser.username,
        rememberMe: true
      });
    }

    const currentUser = AuthService.getCurrentUser();

    if (currentUser) {
      const redirectPath = this.getRedirectPath(currentUser);
      this.setState({ redirect: redirectPath, loading: false });
    }
  }

  handleLogin(formValue: {
    username: string;
    password: string;
    rememberMe: boolean;
  }) {
    const { username, password, rememberMe } = formValue;
    this.setState({
      message: '',
      loading: true
    });

    AuthService.login(username, password, rememberMe).then(
      () => {
        const currentUser = AuthService.getCurrentUser();
        const redirectPath = this.getRedirectPath(currentUser);
        this.setState({ redirect: redirectPath, loading: false });
      },
      error => {
        if (error.response && error.response.status === 402) {
          this.setState({
            requiresTFA: true,
            loading: false,
            qrCodeURL: error.response.data.qrCodeURL,
            username: username,
            tfaConfirmed: error.response.data.tfaConfirmed
          });
        } else {
          const resMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();

          this.setState({
            loading: false,
            message: resMessage
          });
        }
      }
    );
  }

  handleTFA(username: string, otp: string) {
    this.setState({ loading: true });

    AuthService.verifyTFA(username, otp).then(
      () => {
        const currentUser = AuthService.getCurrentUser();
        const redirectPath = this.getRedirectPath(currentUser);
        this.setState({ redirect: redirectPath, loading: false });
      },
      error => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();

        this.setState({
          loading: false,
          message: resMessage
        });
      }
    );
  }

  handleAzureLogin() {
    try {
      const baseUrl = getBackendUrl();

      // getBackendUrl() devuelve la URL con /api/ al final
      const samlUrl = `${baseUrl}auth/saml`;

      window.location.href = samlUrl;
    } catch (error) {
      console.error('Error initiating SAML login:', error);
      this.setState({
        message: 'Error initiating Azure AD login'
      });
    }
  }

  handleManageEngineLogin = () => {
    try {
      const baseUrl = getBackendUrl();

      // getBackendUrl() devuelve la URL con /api/ al final
      const samlUrl = `${baseUrl}auth/manageengine/saml`;

      window.location.href = samlUrl;
    } catch (error) {
      console.error('Error initiating SAML login:', error);
      this.setState({
        message: 'Error initiating Azure AD login'
      });
    }
  };

  render() {
    if (this.state.redirect) {
      return <Navigate to={this.state.redirect} />;
    }

    const { loading, message, requiresTFA, qrCodeURL, tfaConfirmed, username } =
      this.state;

    const initialValues = {
      username: this.state.username || '',
      password: '',
      rememberMe: this.state.rememberMe || false
    };

    // Muestra el formulario de TFA en una segunda fase
    if (requiresTFA) {
      return (
        <>
          <TwoFAForm
            qrCodeURL={qrCodeURL}
            tfaConfirmed={tfaConfirmed}
            username={username}
            onSubmit={this.handleTFA}
          />
          {message && (
            <div className="text-center fs-9 fw-semibold">
              <img src={authShieldError} alt="Sign in error"></img>
              {message}
            </div>
          )}
        </>
      );
    }

    // Muestra el formulario de login
    return (
      <>
        <div className="text-center mb-5">
          <h3 className="text-body-highlight">Sign In</h3>
          <p className="text-body-tertiary">Get access to your account</p>
        </div>

        <div className="col-md-12">
          {import.meta.env.VITE_AZURE_ENABLED === 'true' && (
            <button
              type="button"
              className="w-100 mb-3 p-1 btn btn-phoenix-secondary"
              onClick={this.handleAzureLogin}
            >
              <img
                src={entraidlogologo}
                alt="Azure Entra id Logo"
                className="me-3"
                style={{ height: '26px', width: '26px' }}
              />
              Sign in with Microsoft Azure
            </button>
          )}

          {import.meta.env.VITE_MANAGEENGINE_ENABLED === 'true' && (
            <button
              type="button"
              className="w-100 mb-3 p-1 btn btn-phoenix-secondary"
              onClick={this.handleManageEngineLogin}
            >
              <img
                src={manageenginelogo}
                alt="ManageEngine Logo"
                className="me-3"
                style={{ height: '26px', width: '24px' }}
              />
              Sign in with ManageEngine ADS
            </button>
          )}

          {(import.meta.env.VITE_AZURE_ENABLED === 'true' ||
            import.meta.env.VITE_MANAGEENGINE_ENABLED === 'true') && (
            <div className="position-relative">
              <hr className="my-4" />
              <div className="divider-content-center">or</div>
            </div>
          )}

          <div className="auth-form-box">
            <Formik
              initialValues={initialValues}
              onSubmit={this.handleLogin}
              enableReinitialize={true}
            >
              <Form className="mb-2 text-start">
                <div className="mb-1 text-start">
                  <label htmlFor="username" className="form-label">
                    Username or email
                  </label>
                  <div className="form-icon-container">
                    <FontAwesomeIcon
                      icon={faUser}
                      className="text-body fs-9 form-icon"
                    />
                    <Field
                      name="username"
                      type="text"
                      className="form-icon-input form-control"
                    />
                    <ErrorMessage
                      name="username"
                      component="div"
                      className="alert alert-danger"
                    />
                  </div>
                </div>
                <div className="mb-2 text-start">
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <div className="form-icon-container">
                    <FontAwesomeIcon
                      icon={faKey}
                      className="text-body fs-9 form-icon"
                    />
                    <Field
                      name="password"
                      type="password"
                      className="form-icon-input form-control"
                    />
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="alert alert-danger"
                    />
                  </div>
                </div>

                <Row className="flex-between-center mb-4">
                  <Col xs="auto">
                    <Field
                      name="rememberMe"
                      type="checkbox"
                      id="remember-me"
                      className="form-check-input cmb-0"
                    />
                    <label
                      htmlFor="remember-me"
                      className="mb-0 fs-9 fw-semibold"
                    >
                      &nbsp;&nbsp;Remember me
                    </label>
                  </Col>
                  <Col xs="auto">
                    <Link
                      to={`/authentication/forgot-password`}
                      className="fs-9 fw-semibold"
                    >
                      Forgot Password?
                    </Link>
                  </Col>
                </Row>

                <button
                  type="submit"
                  className="w-100 mb-3 btn btn-primary btn-block"
                  disabled={loading}
                >
                  {loading && (
                    <span className="spinner-border spinner-border-sm"></span>
                  )}
                  <span>&nbsp;&nbsp;Login&nbsp;&nbsp;</span>
                </button>

                <div className="text-center fs-9 fw-semibold">
                  &nbsp;
                  {message && (
                    <>
                      <img src={authShieldError} alt="Sign in error"></img>
                      {message}
                    </>
                  )}
                </div>
              </Form>
            </Formik>
          </div>
        </div>
      </>
    );
  }
}
